import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { buildLocalBusinessSchema } from './SEO';
import { business, services, trustPoints } from './siteData';
import {
  ButtonRow,
  CTASection,
  CTAContent,
  Eyebrow,
  GalleryCard,
  GalleryGrid,
  GalleryImage,
  HeroBadge,
  HeroContent,
  HeroLead,
  HeroMedia,
  HeroSection,
  HomeWrapper,
  PrimaryButton,
  Section,
  SectionHeader,
  SecondaryButton,
  ServiceCard,
  ServiceGrid,
  TrustList,
  WhyCard,
  WhyGrid,
} from './styled/home.style';

const Home = () => {
  const heroService = services.find((service) => service.title === 'Commercial Drywall') || services[0];
  const galleryPreview = services.slice(0, 4);

  return (
    <HomeWrapper>
      <SEO
        title="KMK Drywall | Western New York Drywall Contractor"
        description="KMK Drywall provides residential drywall, commercial drywall, drywall finishing, steel framing, ceiling tile grid, and drywall repair in Buffalo, Erie County, and Western New York."
        path="/"
        schema={buildLocalBusinessSchema('/')}
      />
      <HeroSection>
        <HeroContent>
          <Eyebrow>Springville drywall contractor serving Western New York</Eyebrow>
          <h1>Professional drywall, framing, and ceiling work done cleanly.</h1>
          <HeroLead>
            KMK Drywall helps homeowners, builders, and commercial property teams
            across {business.serviceArea} get reliable drywall installation,
            finishing, steel framing, ceiling grid, and repair work.
          </HeroLead>
          <ButtonRow>
            <PrimaryButton to="/quote">Request a Quote</PrimaryButton>
            <SecondaryButton href={business.phoneHref}>Call {business.phoneDisplay}</SecondaryButton>
          </ButtonRow>
          <TrustList aria-label="Why customers choose KMK Drywall">
            {trustPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </TrustList>
        </HeroContent>
        <HeroMedia>
          <img
            src={heroService.image}
            alt={heroService.alt}
            fetchPriority="high"
          />
          <HeroBadge>
            <strong>Local crews.</strong>
            <span>Responsive quotes and jobsite-ready workmanship.</span>
          </HeroBadge>
        </HeroMedia>
      </HeroSection>

      <Section>
        <SectionHeader>
          <Eyebrow>Drywall services</Eyebrow>
          <h2>Built for residential and commercial projects.</h2>
          <p>
            From drywall repair in a single room to commercial build-outs near Buffalo,
            KMK Drywall keeps the process straightforward, professional, and clean.
          </p>
        </SectionHeader>
        <ServiceGrid>
          {services.map((service) => (
            <ServiceCard as="article" key={service.title}>
              <img src={service.image} alt={service.alt} loading="lazy" />
              <div>
                <h3>{service.title}</h3>
                <p>{service.summary}</p>
                <Link to="/quote">Get pricing for {service.title.toLowerCase()}</Link>
              </div>
            </ServiceCard>
          ))}
        </ServiceGrid>
      </Section>

      <Section>
        <SectionHeader>
          <Eyebrow>Why KMK Drywall</Eyebrow>
          <h2>A local drywall contractor you can reach.</h2>
          <p>
            Good drywall work comes down to planning, communication, straight framing,
            clean seams, and finish details that hold up after paint.
          </p>
        </SectionHeader>
        <WhyGrid>
          <WhyCard>
            <h3>Clear communication</h3>
            <p>
              Phone and email are easy to find, and quote requests go straight to
              the team so your project can move.
            </p>
          </WhyCard>
          <WhyCard>
            <h3>Interior trade focus</h3>
            <p>
              Drywall, finishing, steel framing, ceiling tile grid, and repair
              are the core services, not an afterthought.
            </p>
          </WhyCard>
          <WhyCard>
            <h3>Western New York coverage</h3>
            <p>
              Based in Springville and serving Buffalo, Erie County, and the
              surrounding Western New York area.
            </p>
          </WhyCard>
        </WhyGrid>
      </Section>

      <Section>
        <SectionHeader>
          <Eyebrow>Recent work</Eyebrow>
          <h2>See the kind of projects KMK Drywall handles.</h2>
          <p>
            Browse residential drywall, commercial drywall, steel framing, and
            acoustical ceiling projects from the KMK Drywall photo library.
          </p>
        </SectionHeader>
        <GalleryGrid>
          {galleryPreview.map((service) => (
            <GalleryCard as={Link} to="/about" key={service.title}>
              <GalleryImage src={service.image} alt={service.alt} loading="lazy" />
              <span>{service.title}</span>
            </GalleryCard>
          ))}
        </GalleryGrid>
      </Section>

      <CTASection>
        <CTAContent>
          <Eyebrow>Ready for a cleaner project?</Eyebrow>
          <h2>Tell KMK Drywall what you need and where the job is located.</h2>
          <p>
            Request a drywall quote online, call {business.phoneDisplay}, or email
            the team with project details and photos.
          </p>
          <ButtonRow>
            <PrimaryButton to="/quote">Request a Quote</PrimaryButton>
            <SecondaryButton href={business.emailHref}>Email KMK Drywall</SecondaryButton>
          </ButtonRow>
        </CTAContent>
      </CTASection>
    </HomeWrapper>
  );
};

export default Home;
